import {
  isBlackKingPiece,
  isKingPiece,
  isPiece,
  isWhiteKingPiece,
  oppositePlayer,
  removePieceFromHand,
  resolveConflicts,
} from 'features/board/utils'
import {
  DeployMetadata,
  GameEventType,
  InitializeMetadata,
  MoveMetadata,
  PossibleMove,
  PossibleMoves,
  SwitchMetadata,
  WithdrawMetadata,
} from 'features/firebase/types'
import { HexBoard, HexId } from 'features/hex-board/hex-board'

import { getEntries } from 'shared/utils'

import { BoardState, Kings, initialBoardState } from '../board/board'

type EventFunction<T = any> = (state: BoardState, eventMetadata: T) => BoardState

const initialize: EventFunction<InitializeMetadata> = (_, { walls }) => {
  return {
    ...initialBoardState,
    hexBoard: {
      ...initialBoardState.hexBoard,
      [walls[0]]: 'wall',
      [walls[1]]: 'wall',
    },
  }
}

const distribute: EventFunction<DeployMetadata> = (state, { piece, hexId }) => {
  const hand = {
    black: removePieceFromHand(state.hand.black, piece),
    white: removePieceFromHand(state.hand.white, piece),
  }

  let turn = state.turn
  if (hand[turn].length < hand[oppositePlayer(turn)].length) {
    turn = oppositePlayer(turn)
  }

  return {
    ...state,
    turn,
    hand,
    hexBoard: {
      ...state.hexBoard,
      [hexId]: piece,
    },
  }
}

const phaseTransition: EventFunction = state => {
  return {
    ...state,
    phase: 'fight',
    turn: 'white',
  }
}

const move: EventFunction<MoveMetadata> = (state, { from, to }) => {
  return {
    ...state,
    turn: oppositePlayer(state.turn),
    kingMoved: {
      ...state.kingMoved,
      [state.turn]: isKingPiece(state.hexBoard[from]) ? true : state.kingMoved[state.turn],
    },
    hexBoard: {
      ...state.hexBoard,
      [from]: null,
      [to]: state.hexBoard[from],
    },
  }
}

const makeSwitch: EventFunction<SwitchMetadata> = (state, { from, to }) => {
  return {
    ...state,
    turn: oppositePlayer(state.turn),
    kingMoved: {
      ...state.kingMoved,
      [state.turn]: isKingPiece(state.hexBoard[from]) ? true : state.kingMoved[state.turn],
    },
    hexBoard: {
      ...state.hexBoard,
      [from]: state.hexBoard[to],
      [to]: state.hexBoard[from],
    },
  }
}

const withdraw: EventFunction<WithdrawMetadata> = (state, { player, hexId }) => {
  return {
    ...state,
    turn: oppositePlayer(state.turn),
    hexBoard: {
      ...state.hexBoard,
      [hexId]: null,
    },
    hand: {
      ...state.hand,
      [player]: [...state.hand[player], state.hexBoard[hexId]],
    },
  }
}

const deploy: EventFunction<DeployMetadata> = (state, { piece, hexId }) => {
  const blackHand = removePieceFromHand(state.hand.black, piece)
  const whiteHand = removePieceFromHand(state.hand.white, piece)

  return {
    ...state,
    turn: oppositePlayer(state.turn),
    hand: {
      black: blackHand,
      white: whiteHand,
    },
    hexBoard: {
      ...state.hexBoard,
      [hexId]: piece,
    },
  }
}

const fight: EventFunction = state => {
  return {
    ...state,
    turn: oppositePlayer(state.turn),
    hexBoard: resolveConflicts(state.hexBoard),
    fightClicks: {
      ...state.fightClicks,
      [state.turn]: state.fightClicks[state.turn] + 1,
    },
  }
}

const draw: EventFunction = state => state

export const getGameSwitchRestrictions = (events: PossibleMoves, hexBoard: HexBoard) => {
  const restrictions: [HexId, HexId][] = []

  for (let i = events.length - 1; i >= Math.max(0, events.length - 2); i--) {
    const { type, metadata } = events[i]
    if (type === 'switch' && hexBoard[metadata.from] && hexBoard[metadata.to]) {
      restrictions.push([metadata.from, metadata.to])
    }
  }

  return restrictions
}

export const getKings = (hexBoard: HexBoard) => {
  const kings: Kings = {
    white: {
      piece: null,
      hexId: null,
    },
    black: {
      piece: null,
      hexId: null,
    },
  }

  getEntries(hexBoard).forEach(([hexId, piece]) => {
    if (isWhiteKingPiece(piece)) {
      kings.white.piece = piece
      kings.white.hexId = hexId
    } else if (isBlackKingPiece(piece)) {
      kings.black.piece = piece
      kings.black.hexId = hexId
    }
  })

  return kings
}

const eventTypeFunction: Record<GameEventType, EventFunction> = {
  initialize,
  distribute,
  phaseTransition,
  move,
  switch: makeSwitch,
  withdraw,
  deploy,
  fight,
  draw,
}

const applyEvent: EventFunction<PossibleMove> = (state, event) => eventTypeFunction[event.type](state, event.metadata)

export const computeStateFromEvents = (events: PossibleMoves): BoardState => {
  if (!events.length) {
    return initialBoardState
  }

  const newState = events.reduce((state, event) => applyEvent(state, event), {} as BoardState)
  const kings = getKings(newState.hexBoard)
  const switchRestrictions = getGameSwitchRestrictions(events, newState.hexBoard)
  const pieceCount =
    Object.values(newState.hexBoard).filter(isPiece).length + newState.hand.black.length + newState.hand.white.length

  return {
    ...newState,
    kings,
    pieceCount,
    switchRestrictions,
  }
}

export const addEvent = (state: BoardState, event: PossibleMove): BoardState => {
  const newState = applyEvent(state, event)
  const kings = getKings(newState.hexBoard)
  const switchRestrictions = getGameSwitchRestrictions([event], newState.hexBoard)
  const pieceCount =
    Object.values(newState.hexBoard).filter(isPiece).length + newState.hand.black.length + newState.hand.white.length

  return {
    ...newState,
    kings,
    pieceCount,
    switchRestrictions,
  }
}
